import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  useTheme,
  Slider,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  SentimentSatisfiedAlt as PositiveIcon,
  SentimentNeutral as NeutralIcon,
  SentimentDissatisfied as NegativeIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import {
  getSentimentStatus,
  getSentimentStats,
  analyzeSentimentPending,
  getSystemConfig,
  updateSystemConfig
} from '../../../../services/api';
import { useMIA } from '../../../../context/MIAContext';

const SentimentAnalysisManagement = () => {
  const theme = useTheme();
  const { activated, isSentimentAIEnabled } = useMIA();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [apiStatus, setApiStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState(null);
  const [systemConfig, setSystemConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  // Config
  const [batchSize, setBatchSize] = useState(20);
  const [delayMs, setDelayMs] = useState(1000);
  const sentimentConfig = systemConfig?.sentimentAnalysis || {
    engine: 'nlp',
    preferDeepseek: false,
    allowFallbackToLocal: true
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, statsRes, configRes] = await Promise.all([
        getSentimentStatus(),
        getSentimentStats(),
        getSystemConfig()
      ]);
      setApiStatus(statusRes.data);
      setStats(statsRes.data);
      setSystemConfig(configRes.data.data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar dados: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSentimentConfigChange = (field, value) => {
    setSystemConfig((current) => ({
      ...current,
      sentimentAnalysis: {
        engine: 'nlp',
        preferDeepseek: false,
        allowFallbackToLocal: true,
        ...(current?.sentimentAnalysis || {}),
        [field]: value
      }
    }));
  };

  const handleSaveSentimentConfig = async () => {
    if (!systemConfig) return;

    try {
      setSavingConfig(true);
      setError('');
      setSuccess('');
      await updateSystemConfig({
        sentimentAnalysis: sentimentConfig
      });
      setSuccess('Configuração de sentimento salva com sucesso.');
      await fetchData();
    } catch (err) {
      setError('Erro ao salvar configuração: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAnalyzePending = async () => {
    // Verifica se MIA está ativo antes de tentar analisar
    if (!activated || !isSentimentAIEnabled()) {
      setError('Ative sua MIA (Mensure IA) para usar a análise de sentimento.');
      return;
    }

    setAnalyzing(true);
    setProgress({ current: 0, total: batchSize });
    setError('');
    setSuccess('');

    try {
      const response = await analyzeSentimentPending(batchSize, delayMs, sentimentConfig);
      const { results, success: successCount, errors } = response.data;

      setProgress({ current: successCount + errors, total: batchSize });

      if (errors > 0) {
        setSuccess(`Analise concluida: ${successCount} sucesso, ${errors} erro(s)`);
      } else {
        setSuccess(`${successCount} artigos analisados com sucesso!`);
      }

      // Recarregar estatisticas
      await fetchData();
    } catch (err) {
      // Trata resposta de MIA não ativo
      if (err.response?.data?.miaRequired) {
        setError(err.response.data.message || 'Ative sua MIA (Mensure IA) para usar esta funcionalidade.');
      } else {
        setError('Erro ao analisar artigos: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setAnalyzing(false);
      setProgress(null);
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return theme.palette.success.main;
      case 'negative': return theme.palette.error.main;
      default: return theme.palette.warning.main;
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return <PositiveIcon />;
      case 'negative': return <NegativeIcon />;
      default: return <NeutralIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AutoAwesomeIcon sx={{ fontSize: 32, color: '#8B5CF6', mr: 1.5 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Análise de Sentimento (Gemini AI)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Processamento de sentimento com IA
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      {/* Alerta MIA não ativo */}
      {!activated && (
        <Alert
          severity="warning"
          icon={<LockIcon />}
          sx={{
            mb: 2,
            background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.main}15 100%)`,
            border: `1px solid ${theme.palette.warning.main}40`
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Ative sua MIA (Mensure IA) para usar a análise de sentimento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A análise de sentimento com IA é uma funcionalidade exclusiva do MIA. Entre em contato com a equipe Mensure para obter seu token de ativação.
          </Typography>
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Status da API */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PsychologyIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6">Status da API</Typography>
              </Box>

              {apiStatus?.available ? (
                <Box>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="API Configurada"
                    color="success"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Motor ativo: {sentimentConfig.engine === 'ai' ? 'IA/MIA' : 'NLP local'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fallback: {sentimentConfig.allowFallbackToLocal ? 'NLP local habilitado' : 'desabilitado'}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Chip
                    icon={<ErrorIcon />}
                    label="API Nao Configurada"
                    color="error"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Configure GEMINI_API_KEY no .env
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Estatisticas */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AutoAwesomeIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
                <Typography variant="h6">Estatisticas</Typography>
              </Box>

              {stats && (
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h4" color="primary">
                        {stats.total || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Artigos
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h4" sx={{ color: theme.palette.success.main }}>
                        {stats.analyzed || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Analisados
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h4" sx={{ color: theme.palette.warning.main }}>
                        {stats.pending || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pendentes
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h4" sx={{ color: theme.palette.info.main }}>
                        {stats.total > 0 ? Math.round((stats.analyzed / stats.total) * 100) : 0}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cobertura
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Configuracao do motor */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h6">
                    Motor de Análise
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Escolha se a MIA vai classificar com IA ou com o NLP local. Essa escolha vale para o botão dos cards e para o lote.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleSaveSentimentConfig}
                  disabled={savingConfig || !systemConfig}
                  startIcon={savingConfig ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                >
                  {savingConfig ? 'Salvando...' : 'Salvar Configuração'}
                </Button>
              </Box>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="sentiment-engine-label">Motor padrão</InputLabel>
                    <Select
                      labelId="sentiment-engine-label"
                      label="Motor padrão"
                      value={sentimentConfig.engine}
                      onChange={(event) => handleSentimentConfigChange('engine', event.target.value)}
                    >
                      <MenuItem value="nlp">NLP local</MenuItem>
                      <MenuItem value="ai">IA/MIA</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sentimentConfig.preferDeepseek}
                        onChange={(event) => handleSentimentConfigChange('preferDeepseek', event.target.checked)}
                        disabled={sentimentConfig.engine !== 'ai'}
                      />
                    }
                    label="Priorizar DeepSeek antes do Gemini"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sentimentConfig.allowFallbackToLocal}
                        onChange={(event) => handleSentimentConfigChange('allowFallbackToLocal', event.target.checked)}
                      />
                    }
                    label="Usar NLP local se a IA falhar"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                {(apiStatus?.aiModels || []).map((item) => (
                  <Chip
                    key={`${item.provider}-${item.model}`}
                    label={`${item.provider}: ${item.model}${item.configured ? '' : ' (sem chave)'}`}
                    color={item.configured ? 'success' : 'default'}
                    variant={item.configured ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Distribuicao de Sentimentos */}
        {stats?.bySentiment && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Distribuicao de Sentimentos
                </Typography>
                <Grid container spacing={2}>
                  {['positive', 'neutral', 'negative'].map((sentiment) => {
                    const sentimentData = stats.bySentiment[sentiment];
                    // Suporta tanto número direto quanto objeto {count, avgConfidence}
                    const count = typeof sentimentData === 'object'
                      ? (sentimentData?.count || 0)
                      : (sentimentData || 0);
                    const avgConfidence = typeof sentimentData === 'object'
                      ? (sentimentData?.avgConfidence || 0)
                      : 0;
                    const percentage = stats.analyzed > 0
                      ? Math.round((count / stats.analyzed) * 100)
                      : 0;

                    return (
                      <Grid item xs={12} sm={4} key={sentiment}>
                        <Box sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: `${getSentimentColor(sentiment)}15`,
                          border: `1px solid ${getSentimentColor(sentiment)}30`
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ color: getSentimentColor(sentiment), mr: 1 }}>
                              {getSentimentIcon(sentiment)}
                            </Box>
                            <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                              {sentiment === 'positive' ? 'Positivo' :
                               sentiment === 'negative' ? 'Negativo' : 'Neutro'}
                            </Typography>
                          </Box>
                          <Typography variant="h4" sx={{ color: getSentimentColor(sentiment) }}>
                            {count}
                          </Typography>
                          {avgConfidence > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Confianca media: {Math.round(avgConfidence * 100)}%
                            </Typography>
                          )}
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              mt: 1,
                              height: 8,
                              borderRadius: 4,
                              bgcolor: `${getSentimentColor(sentiment)}20`,
                              '& .MuiLinearProgress-bar': {
                                bgcolor: getSentimentColor(sentiment),
                                borderRadius: 4
                              }
                            }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {percentage}% dos analisados
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Analise em Lote */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Analisar Artigos Pendentes
              </Typography>

              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography gutterBottom>
                    Quantidade por lote: {batchSize}
                  </Typography>
                  <Slider
                    value={batchSize}
                    onChange={(e, val) => setBatchSize(val)}
                    min={5}
                    max={50}
                    step={5}
                    marks
                    disabled={analyzing || !activated}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography gutterBottom>
                    Delay entre requests: {delayMs}ms
                  </Typography>
                  <Slider
                    value={delayMs}
                    onChange={(e, val) => setDelayMs(val)}
                    min={500}
                    max={3000}
                    step={250}
                    marks
                    disabled={analyzing || !activated}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={!activated ? <LockIcon /> : analyzing ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={handleAnalyzePending}
                    disabled={analyzing || !apiStatus?.available || (stats?.pending || 0) === 0 || !activated}
                    fullWidth
                    size="large"
                  >
                    {!activated
                      ? 'MIA Necessário'
                      : analyzing
                        ? 'Analisando...'
                        : `Analisar ${Math.min(batchSize, stats?.pending || 0)} Pendentes`
                    }
                  </Button>
                </Grid>
              </Grid>

              {progress && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Progresso: {progress.current} / {progress.total}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(progress.current / progress.total) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}

              {!apiStatus?.available && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Configure a GEMINI_API_KEY no arquivo .env do backend para habilitar a analise de sentimento.
                </Alert>
              )}

              {(stats?.pending || 0) === 0 && apiStatus?.available && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Todos os artigos ja foram analisados! Novos artigos serao analisados automaticamente durante o scraping.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SentimentAnalysisManagement;
